import React from "react";
import { useRecoilValue } from "recoil";
import { recoilStates } from "src/recoil/recoilStates";
import styled from "styled-components";

interface PrivacyConsentProps {
  clubName?: string;
  clubInfo?: { name?: string };
}

export default function PrivacyConsent({ clubInfo }: PrivacyConsentProps) {
  // CancellationPolicy.tsx 참고한 방식으로 clubInfo 조회
  const bookingApplication = useRecoilValue(recoilStates.bookingApplication);
  const clubRelData =
    bookingApplication?.clubRels?.find((cr: any) => [4, 6].includes(cr.year_booking_status_id)) ||
    bookingApplication?.clubRels?.[0];
  const clubInfoFromRecoil = clubRelData?.club?.[0];
  const displayClubName = clubInfoFromRecoil?.name || clubInfo?.name || "골프장명";
  return (
    <Container>
      <div className="content">
        <h2 className="page-title">개인정보 제3자 제공 동의</h2>

        {/* 1. 제공 대상 및 제공 목적 */}
        <section className="policy-section">
          <h3>1. 제공 대상 및 제공 목적</h3>
          <div className="policy-item">
            <p>
              ㈜에이치비엠피(이하 '회사')는 「개인정보 보호법」 제17조 및 제18조에 따라 다음과 같은
              경우에만 개인정보를 제3자에게 제공합니다.
            </p>
            <ul>
              <li>
                서비스 제공 및 회원 식별, 본인 확인, 결제·정산 등 계약의 이행을 위해 필요한 경우
              </li>
              <li>법령에 특별한 규정이 있거나 수사기관의 적법한 절차에 따른 요청이 있는 경우</li>
              <li>기타 정보주체가 별도로 동의한 경우</li>
            </ul>
          </div>
        </section>

        {/* 2. 제공받는 자 */}
        <section className="policy-section">
          <h3>2. 제공받는 자</h3>
          <div className="policy-item">
            <ul>
              <li>카카오, 네이버, 애플(소셜 로그인 및 중복가입 확인 목적), 예약 골프장</li>
              <li>결제대행사 ㈜다날(구매 및 요금 결제)</li>
              <li>본인인증기관 나이스평가정보㈜(실명 확인 및 본인 인증)</li>
              <li>SMS 발송 대행사 비즈톡㈜(알림·인증 문자 발송)</li>
            </ul>
          </div>
        </section>

        {/* 3. 제공 항목 */}
        <section className="policy-section">
          <h3>3. 제공 항목</h3>
          <div className="policy-item">
            <ul>
              <li>
                <strong>카카오:</strong> 카카오계정(이메일), 닉네임, 프로필사진, 전화번호 등(선택:
                생일, 성별, 출생연도, 이름)
              </li>
              <li>
                <strong>네이버:</strong> 출생연도, 휴대전화번호, 이름, 이메일주소, 별명, 프로필사진,
                성별, 생일, 연령대 등
              </li>
              <li>
                <strong>애플:</strong> 이메일, 휴대전화번호, 성별, 생년월일, 이름, CI
              </li>
              <li>
                <strong>결제·본인인증:</strong> 카드정보, 휴대폰 번호, 명의자 성명 등 결제·인증 시
                필요한 정보
              </li>
            </ul>
          </div>
        </section>

        {/* 4. 보유 및 이용기간 */}
        <section className="policy-section">
          <h3>4. 보유 및 이용기간</h3>
          <div className="policy-item">
            <ul>
              <li>제공 목적 달성 시 또는 동의 철회·회원 탈퇴 시까지 지체 없이 파기</li>
              <li>단, 관련 법령에 의해 일정 기간 보존이 필요한 경우 해당 법령에 따라 보관</li>
            </ul>
          </div>
        </section>

        {/* 5. 동의 거부 권리 및 불이익 */}
        <section className="policy-section">
          <h3>5. 동의 거부 권리 및 불이익</h3>
          <div className="policy-item">
            <p>
              정보주체는 위 제3자 제공에 대한 동의를 거부할 권리가 있습니다. 다만, 동의를 거부할
              경우 회원가입, 결제, 본인 인증 등 서비스 이용이 제한될 수 있습니다.
            </p>
          </div>
        </section>

        {/* 추가 안내 */}
        <section className="policy-section">
          <div className="policy-item">
            <p>
              회사는 서비스 이용자의 개인정보를 골프장 예약 서비스의 계약 이행 및 조율을 위해 서비스
              예약/결제 시 개인정보 제공에 대한 동의를 구한 후 아래의 정보를 제3자에게 제공하고
              있습니다. 이용자는 해당 개인정보 제3자 제공에 대한 동의를 거부하실 수 있으며, 이 경우
              골프장 예약 서비스 이용이 제한될 수 있습니다.
            </p>
            <p className="em">
              해당 서비스의 개인정보 제3자 제공에 대한 약관 정보를 모두 확인하였으며, 개인정보 제3자
              제공에 동의합니다.
            </p>
          </div>
        </section>

        {/* 하단 표: 제공받는 자 */}
        <section className="policy-section">
          <InfoTable>
            <InfoRow>
              <InfoLabel>제공받는 자</InfoLabel>
              <InfoValue>{displayClubName}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>이용 목적</InfoLabel>
              <InfoValue>골프장 예약 확인 및 안내</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>이용 항목</InfoLabel>
              <InfoValue>예약자 성명, 연락처</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>보유 및 이용기간</InfoLabel>
              <InfoValue>
                회원탈퇴, 또는 동의 철회시까지
                <br />
                단, 관계 법령이 정한 시점까지 보존
              </InfoValue>
            </InfoRow>
          </InfoTable>
        </section>
      </div>
    </Container>
  );
}

const Container = styled.div`
  min-height: 100vh;
  background: #fdfdfd;
  padding: 16px;
  font-family: "Pretendard", -apple-system, Roboto, Helvetica, sans-serif;

  .page-title {
    font-size: 20px;
    font-weight: 800;
    margin: 0 0 16px 0;
  }

  .content {
    margin-bottom: 24px;
    padding-bottom: 40px;
  }

  .policy-section {
    margin-bottom: 24px;
  }

  h3 {
    color: #111827;
    font-size: 16px;
    font-weight: 700;
    margin: 0 0 12px 0;
  }

  p {
    color: #1f2937;
    font-size: 14px;
    line-height: 21px;
    margin: 0 0 8px 0;
  }

  p.em {
    font-weight: 600;
    color: #111827;
  }

  ul {
    margin: 8px 0;
    padding-left: 20px;
  }

  li {
    color: #1f2937;
    font-size: 14px;
    line-height: 21px;
    margin-bottom: 6px;
  }

  strong {
    font-weight: 700;
  }
`;

const InfoTable = styled.div`
  border: 1px solid #d1d5db;
  border-radius: 8px;
  overflow: auto; /* 좌우 스크롤 허용 */
  max-width: 100%;
`;

const InfoRow = styled.div`
  display: flex;
  min-width: 520px; /* 표 전체 최소 너비 증가 */
  border-bottom: 1px solid #d1d5db;

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.div`
  width: 140px;
  padding: 12px;
  background-color: #f9fafb;
  border-right: 1px solid #d1d5db;
  font-weight: 500;
  font-size: 14px;
  color: #374151;
  white-space: nowrap;
`;

const InfoValue = styled.div`
  flex: 1;
  padding: 12px;
  font-size: 14px;
  color: #374151;
  max-height: 96px;
  overflow-y: auto;
  overflow-x: auto; /* 가로 스크롤 */
  min-width: 320px; /* 값 칸 기본 너비 증가 */
`;
